import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_WATCH_AREAS, type WatchArea } from '@ww/shared';
import { api, type AreaMatch } from '../api';
import { ErrorBox, Toggle } from './ui';
import { errorMessage } from '../lib/status';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Extra alert locations on top of the coverage area.
 *
 * `allowCounty` is the super-admin view (Tenant Detail). Tenant admins only get the city
 * search, and the server refuses anything outside their county regardless of what this
 * component shows.
 */
export default function WatchAreasPanel({
  state,
  tenantId,
  allowCounty,
  readOnly,
  coverageIsCounty,
}: {
  state: string;
  /** Act on this tenant instead of the acting one (super-admin on Tenant Detail). */
  tenantId?: string;
  allowCounty: boolean;
  readOnly: boolean;
  /** When coverage is already the whole county, a city inside it adds nothing. */
  coverageIsCounty: boolean;
}) {
  const [list, setList] = useState<WatchArea[] | null>(null);
  const [kind, setKind] = useState<'city' | 'county'>('city');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AreaMatch[] | null>(null);
  const [smsOnAdd, setSmsOnAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);
  const st = state.trim().toUpperCase();

  const load = useCallback(async () => {
    try {
      setList((await api.watchAreas(tenantId)).watchAreas);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (readOnly || st.length !== 2 || q.trim().length < 2) {
      setResults(null);
      return;
    }
    const id = (reqId.current += 1);
    const timer = window.setTimeout(async () => {
      try {
        const r = await api.searchAreas(kind, st, q.trim());
        if (reqId.current === id) setResults(r.areas ?? []);
      } catch (err) {
        if (reqId.current === id) setError(errorMessage(err));
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [kind, st, q, readOnly]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const add = (m: AreaMatch) =>
    run(async () => {
      await api.addWatchArea({ kind: m.kind as 'city' | 'county', geoid: m.geoid, sendSms: smsOnAdd }, tenantId);
      setQ('');
      setResults(null);
    });

  const full = (list?.length ?? 0) >= MAX_WATCH_AREAS;

  return (
    <section className="panel" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ fontWeight: 600 }}>Extra alert locations</div>
        <div className="muted" style={{ fontSize: 13 }}>
          Alerts for these places show on the dashboard and sound, labelled with the place name. SMS goes out
          only where it is switched on.
          {!allowCounty && ' Need another county? Message the WeatherWatch administrators.'}
          {coverageIsCounty &&
            !allowCounty &&
            ' Your coverage is already the whole county, so cities inside it are covered without adding them.'}
        </div>
      </div>
      <ErrorBox error={error} />

      {list && list.length === 0 && <div className="muted">No extra locations yet.</div>}
      {list && list.length > 0 && (
        <div className="scroll" style={{ maxHeight: 180, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((w) => {
            // Counties are the WeatherWatch admins' to remove; the server enforces this too.
            const canRemove = !readOnly && (allowCounty || w.area.kind === 'city');
            return (
              <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flexGrow: 1 }}>
                  {w.area.label}{' '}
                  <span className="muted" style={{ fontSize: 12 }}>
                    {w.area.kind === 'county' ? 'County' : 'City'}
                  </span>
                </span>
                <span className="muted" style={{ fontSize: 12 }}>
                  SMS
                </span>
                <Toggle
                  on={w.sendSms}
                  label={`Send SMS for ${w.area.label}`}
                  disabled={busy || readOnly}
                  onChange={(next) => void run(() => api.updateWatchArea(w.id, { sendSms: next }, tenantId))}
                />
                {canRemove && (
                  <button
                    type="button"
                    className="btn btn-ghost hov"
                    disabled={busy}
                    onClick={() => void run(() => api.deleteWatchArea(w.id, tenantId))}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && !full && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {allowCounty && (
              <div className="area-kinds" role="group" aria-label="Location type">
                {(['city', 'county'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className="chip hov"
                    aria-pressed={kind === k}
                    onClick={() => {
                      setKind(k);
                      setResults(null);
                    }}
                  >
                    {k === 'city' ? 'City' : 'County'}
                  </button>
                ))}
              </div>
            )}
            <input
              className="fld"
              style={{ flexGrow: 1, minWidth: 160 }}
              value={q}
              placeholder={kind === 'city' ? `Add a city in ${allowCounty ? st : 'your county'}` : `Add a county in ${st}`}
              onChange={(e) => setQ(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              SMS
            </span>
            <Toggle on={smsOnAdd} label="Send SMS for the new location" onChange={setSmsOnAdd} />
          </div>
          {results && (
            <div className="scroll area-results" style={{ maxHeight: 168 }}>
              {results.length === 0 && (
                <div className="muted" style={{ padding: '8px 10px' }}>
                  No matches.
                </div>
              )}
              {results.map((m) => (
                <button
                  key={m.geoid}
                  type="button"
                  className="area-result hov"
                  disabled={busy || list?.some((w) => w.area.geoid === m.geoid)}
                  onClick={() => void add(m)}
                >
                  <span>{m.name}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {list?.some((w) => w.area.geoid === m.geoid) ? 'Added' : 'Add'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {full && <div className="muted">Limit of {MAX_WATCH_AREAS} extra locations reached.</div>}
    </section>
  );
}
